import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, RefreshCw, ArrowRight, CheckCircle2, XCircle, Image as ImageIcon, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [quizDone, setQuizDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [quizMode, setQuizMode] = useState("classic");
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(console.error);
  }, []);

  const startQuiz = async () => {
    setLoading(true);
    try {
      const params = selectedCat === "all" ? { count: 10 } : { category: selectedCat, count: 10 };
      if (quizMode === "picture") params.matched_only = true;
      const res = await axios.get(`${API}/quiz`, { params });
      setQuestions(res.data);
      setCurrentQ(0); setScore(0); setSelected(null); setAnswered(false); setQuizDone(false); setStarted(true);
    } catch (e) { console.error("Failed to load quiz", e); }
    setLoading(false);
  };

  const handleAnswer = (option) => {
    if (answered) return;
    setSelected(option); setAnswered(true);
    if (option === questions[currentQ].swedish) setScore((s) => s + 1);
  };

  const nextQuestion = () => {
    if (currentQ < questions.length - 1) { setCurrentQ((q) => q + 1); setSelected(null); setAnswered(false); }
    else {
      setQuizDone(true);
      if (user) {
        axios.post(`${API}/progress/quiz`, { category: selectedCat === "all" ? null : selectedCat, score, total: questions.length }, { withCredentials: true }).catch(console.error);
      }
    }
  };

  const question = questions[currentQ];
  const progress = questions.length > 0 ? ((currentQ + 1) / questions.length) * 100 : 0;
  const scorePercent = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;

  const getOptionClass = (option) => {
    if (!answered) return "bg-nordic-paper border-nordic-wood/20 text-nordic-text hover:border-nordic-blue/40";
    if (option === questions[currentQ].swedish) return "correct";
    if (option === selected && option !== questions[currentQ].swedish) return "incorrect";
    return "bg-nordic-paper/50 border-nordic-wood/10 text-nordic-text-muted";
  };

  if (!started) {
    return (
      <div className="pt-24 pb-16 min-h-screen" data-testid="quiz-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">Quiz</p>
            <h1 className="font-heading text-4xl md:text-5xl font-medium text-nordic-text tracking-tight mb-4">Test Your Knowledge</h1>
            <p className="text-nordic-text-secondary text-base md:text-lg max-w-md mx-auto mb-12">
              See how many Swedish words you can recognize from their images.
            </p>
            <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 shadow-sm p-10 max-w-md mx-auto" data-testid="quiz-setup">
              <h3 className="font-heading text-xl text-nordic-text mb-6">Choose a category</h3>
              <div className="grid grid-cols-2 gap-2 mb-6" data-testid="quiz-mode-toggle">
                <button
                  onClick={() => setQuizMode("classic")}
                  className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 text-sm font-medium ${
                    quizMode === "classic" ? "border-nordic-blue bg-nordic-blue/5 text-nordic-text" : "border-nordic-wood/20 text-nordic-text-secondary hover:border-nordic-wood/40"
                  }`}
                  style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                  data-testid="quiz-mode-classic"
                >
                  <BookOpen className="w-5 h-5" /> Classic
                  <span className="text-xs font-normal text-nordic-text-muted">Image + English hint</span>
                </button>
                <button
                  onClick={() => setQuizMode("picture")}
                  className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 text-sm font-medium ${
                    quizMode === "picture" ? "border-nordic-blue bg-nordic-blue/5 text-nordic-text" : "border-nordic-wood/20 text-nordic-text-secondary hover:border-nordic-wood/40"
                  }`}
                  style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                  data-testid="quiz-mode-picture"
                >
                  <ImageIcon className="w-5 h-5" /> Picture Only
                  <span className="text-xs font-normal text-nordic-text-muted">No hint — harder!</span>
                </button>
              </div>
              <Select value={selectedCat} onValueChange={setSelectedCat}>
                <SelectTrigger className="w-full rounded-xl bg-nordic-bg border-nordic-wood/30 mb-6" data-testid="quiz-category-select">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>{cat.name} ({cat.word_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={startQuiz} disabled={loading}
                className="w-full bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full py-6 text-lg font-medium"
                data-testid="start-quiz-btn">
                {loading ? "Loading..." : "Start Quiz"}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-nordic-text-muted">Question {currentQ + 1} of {questions.length}</span>
          <div className="flex items-center gap-2" data-testid="quiz-score">
            <Trophy className="w-4 h-4 text-nordic-accent" />
            <span className="text-sm font-medium text-nordic-text">{score} correct</span>
          </div>
        </div>
        <Progress value={progress} className="h-1.5 mb-10 bg-nordic-wood/20" data-testid="quiz-progress" />

        {question && (
          <AnimatePresence mode="wait">
            <motion.div key={currentQ} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <div className="rounded-2xl overflow-hidden shadow-sm border border-nordic-wood/10 mb-8" data-testid="quiz-image">
                <img src={question.image_url} alt="What is this word?" className="w-full h-64 sm:h-80 object-cover"
                  onError={(e) => { const f = "https://images.unsplash.com/photo-1665064914191-6512af753429?w=600&fit=crop&q=80"; if (e.target.src !== f) e.target.src = f; }} />
              </div>
              <h2 className="font-heading text-2xl text-nordic-text text-center mb-2" data-testid="quiz-question-text">
                What Swedish word matches this image?
              </h2>
              {quizMode === "picture" ? (
                <p className="text-center text-nordic-text-muted text-sm mb-8" data-testid="quiz-picture-mode-label">
                  {answered ? (<>English: <span className="font-medium text-nordic-text-secondary">{question.english}</span></>) : "Picture-only mode — no hints!"}
                </p>
              ) : (
                <p className="text-center text-nordic-text-muted text-sm mb-8">
                  English: <span className="font-medium text-nordic-text-secondary">{question.english}</span>
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="quiz-options">
                {question.options.map((option) => (
                  <button key={option} onClick={() => handleAnswer(option)} disabled={answered}
                    className={`quiz-option p-5 rounded-2xl border-2 text-left font-heading text-xl font-medium ${getOptionClass(option)}`}
                    data-testid={`quiz-option-${option}`}>
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {answered && option === question.swedish && <CheckCircle2 className="w-5 h-5 text-white" />}
                      {answered && option === selected && option !== question.swedish && <XCircle className="w-5 h-5 text-white" />}
                    </div>
                  </button>
                ))}
              </div>
              {answered && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center">
                  <Button onClick={nextQuestion} className="bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full px-8 py-5 text-lg" data-testid="next-question-btn">
                    {currentQ < questions.length - 1 ? (<>Next <ArrowRight className="ml-2 w-5 h-5" /></>) : "See Results"}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        <Dialog open={quizDone} onOpenChange={setQuizDone}>
          <DialogContent className="sm:max-w-md rounded-2xl" data-testid="quiz-results-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-center">
                {scorePercent >= 80 ? "Fantastiskt!" : scorePercent >= 50 ? "Bra jobbat!" : "Keep practicing!"}
              </DialogTitle>
            </DialogHeader>
            <div className="text-center pt-4">
              <div className="mb-6">
                <div className="text-6xl font-heading font-medium text-nordic-blue mb-2">{score}/{questions.length}</div>
                <span className="text-nordic-text-secondary">{scorePercent}% correct</span>
              </div>
              <div className="flex gap-3 justify-center">
                <Button onClick={startQuiz} className="bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full px-6" data-testid="retry-quiz-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                </Button>
                <Button variant="outline" onClick={() => { setQuizDone(false); setStarted(false); }} className="rounded-full border-nordic-wood/30" data-testid="new-quiz-btn">
                  New Quiz
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
